<script setup>

import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {emitter} from "@/emitter";
import store from "@/vuex/store";
import {timeFromTs} from "@/helpers";
import HiPlayer from "@/components/Player/HiPlayer.vue";

const showModal = ref(false);

const clip = ref(null)

const trim = ref(null)
const duration = ref(0)
const isAdmin = computed(() => store.getters["live/getIsAdmin"])
const showExtraModal = ref(false)
const playerTime = ref(0)
const playerLoaded = ref(false)
onMounted(() => {
  emitter.on('showClipModal', (data) => setVisibleModal(data));
})

onBeforeUnmount(() => {
  emitter.off('showClipModal', (data) => setVisibleModal(data));
})

function setVisibleModal(data) {
  clip.value = data;
  duration.value = Math.floor(data.duration * 1000)
  showModal.value = true
}

function isTrimChanged() {
  return trim.value?.[0] !== 0 || trim.value?.[1] !== clip.value.duration * 1000
}

function save() {
  let _clip = {...clip.value}
  if (isTrimChanged()) {
    _clip.trim = {
      start: trim.value[0] || 0,
      end: trim.value[1] || duration.value
    }
  } else {
    _clip.trim = null
  }
  store.commit('live/updateClip', {
    id: clip.value.id,
    clip: _clip
  })
  showModal.value = false
}

function pickTime(type) {
  let time = playerTime.value * 1000 || 0;
  if (type === 'start') trim.value[0] = time
  else trim.value[1] = time
}

watch(showModal, (v) => {
  if (v && clip.value) {
    trim.value = [(clip.value.trim?.start || 0), (clip.value.trim?.end || duration.value)]
    store.commit('video/setVideo', {
      url: clip.value.video_url,
      type: 'video/mp4'
    })
  } else {
    clip.value = null
  }
})


</script>

<template>
  <transition name="fade">
    <div v-if="showModal"
         class="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-50 p-4"
    >
      <div
        class="bg-white p-8 rounded-[4px] shadow-2xl max-w-[600px] w-full relative overflow-hidden">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
          <div class="flex gap-2 items-center">
            <h3 class="text-xl font-bold text-dark_blue">Clip settings</h3>
            <i v-if="isAdmin" class="fa fa-eye text-[12px] bg-blue-450 text-white p-1.5 rounded-[4px] cursor-pointer" @click="console.log(clip); showExtraModal = true"></i>
          </div>
          <button @click="showModal = false" class="text-slate-500 hover:text-red-400 transition-colors">
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>
        <div v-if="clip" class="text-[14px] text-black-victor space-y-2 mb-2">
          <!--          <div class="flex gap-2 justify-between">-->
          <!--            <p><strong class="inline-block min-w-[80px]">Start time: </strong>-->
          <!--              {{ timeFromTs(0, true, true) }}</p>-->
          <!--            <p><strong class="inline-block min-w-[80px]">End time: </strong>-->
          <!--              {{ timeFromTs(duration, true, true) }}</p>-->
          <!--          </div>-->

        </div>

        <div class="w-full" style="aspect-ratio: 16 / 9;">
          <HiPlayer @timeupdate="t => playerTime = t" @loaded="playerLoaded = true"/>
        </div>
        <div v-if="clip" class="text-[14px] text-black-victor space-y-2 mt-2">
          <p><strong class="inline-block min-w-[80px] text-dark_blue">Duration: </strong>
            {{ timeFromTs(trim[1] - trim[0], true, true) }}</p>
        </div>

        <div class="mt-4">
          <span class="text-dark_blue font-bold">Trim</span>
          <div class="flex flex-col gap-4" v-if="clip">
            <div class="flex justify-between gap-2 text-[14px] text-black-victor">
              <div class="flex gap-4 items-center">
                <span v-if="trim">{{ timeFromTs(trim[0], true, true) }}</span>
                <el-tooltip content="Pick start time" popper-class="blue-tooltip" placement="top">
                  <i class="fa fa-eye-dropper text-white p-1 rounded-[4px] bg-blue-450 cursor-pointer"
                     @click="pickTime('start')"></i>
                </el-tooltip>
              </div>
              <div class="flex gap-4 items-center">
                <el-tooltip content="Pick end time" popper-class="blue-tooltip" placement="top">
                  <i class="fa fa-eye-dropper text-white p-1 rounded-[4px] bg-blue-450 cursor-pointer"
                     @click="pickTime('end')"></i>
                </el-tooltip>
                <span v-if="trim">{{ timeFromTs(trim[1], true, true) }}</span>
              </div>
            </div>
            <div class="px-2">
              <el-slider v-model="trim" range :min="0" :max="clip.duration*1000" placement="bottom"
                         :format-tooltip="(v) => timeFromTs(v, true, true)"/>
            </div>

          </div>
        </div>

        <hr>

        <div class="flex gap-2 justify-end mt-4">

          <button @click="showModal = false" class="py-3 px-5 rounded-[4px] bg-orange-450 text-white font-bold text-sm">
            Cancel
          </button>
          <button @click="save" class="py-3 px-5 rounded-[4px] bg-blue-450 text-white font-bold text-sm">
            Save
          </button>
        </div>

      </div>
    </div>
  </transition>

  <transition name="fade">
    <div v-if="showExtraModal"
         class="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-50 p-4"
         @click.self="showExtraModal = false"
    >
      <div
        class="bg-white p-8 rounded-[4px] shadow-2xl max-w-[600px] w-full relative overflow-visible">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <h3 class="text-xl font-bold text-dark_blue">Clip info</h3>
          <button @click="showExtraModal = false" class="text-slate-500 hover:text-red-400 transition-colors">
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>

        <pre class="text-[12px]">
          {{clip}}
        </pre>
        <div class="text-[14px] space-y-2 mt-2">
          <p>Start: <strong>{{(clip.startTimestamp / 1000).toFixed(0)}}</strong></p>
          <p>Stop: <strong>{{(clip.endTimestamp / 1000).toFixed(0)}}</strong></p>
          <p>Duration: <strong>{{((clip.endTimestamp - clip.startTimestamp) / 1000).toFixed(0)}}</strong></p>
        </div>

        <hr>

        <div class="flex gap-2 justify-end mt-4">
          <button @click="showExtraModal = false" class="py-3 px-5 rounded-[4px] bg-orange-450 text-white font-bold text-sm">
            Close
          </button>
        </div>

      </div>
    </div>
  </transition>
</template>
